import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PortalLayout from "../components/PortalLayout";
import { api, formatApiErrorDetail, formatDate } from "../lib/api";
import { useI18n } from "../i18n/LanguageContext";
import { useAuth } from "../auth/AuthContext";

const RSVP_STATUS_STYLE = {
  pending: { color: "var(--br-bronze)", border: "var(--br-bronze)" },
  accepted: { color: "var(--br-bronze-soft)", border: "var(--br-bronze-soft)" },
  declined: { color: "var(--br-red)", border: "var(--br-red)" },
};

export default function EventsPage() {
  const { t, locale } = useI18n();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [rsvpModal, setRsvpModal] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/events/me");
      setData(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const cancelRsvp = async (eventId) => {
    if (!window.confirm("Confermi di voler ritirare la richiesta?")) return;
    try {
      await api.delete(`/events/${eventId}/rsvp`);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  if (!data) {
    return (
      <PortalLayout>
        <div className="max-w-[1080px] mx-auto px-6 sm:px-8 pt-14 pb-8" />
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <section
        className="max-w-[1080px] mx-auto px-6 sm:px-8 pt-14 pb-8"
        data-testid="events-page"
      >
        <div className="br-fade-up">
          <div className="br-overline" data-testid="events-overline">{t("events.overline")}</div>
          <h1
            className="font-serif-display text-[clamp(2.4rem,4.5vw,3.8rem)] mt-3 leading-[1.05]"
            data-testid="events-title"
          >
            {t("events.title")}
          </h1>
          <p className="text-[1.02rem] text-[color:var(--br-text)] mt-4 max-w-[700px] leading-relaxed">
            {t("events.intro")}
          </p>
        </div>

        {!data.eligible ? (
          <div
            className="mt-10 br-card p-8 text-center"
            style={{ borderStyle: "dashed", borderColor: "var(--br-line-bright)" }}
            data-testid="events-locked-card"
          >
            <div
              className="font-mono-fin text-[0.66rem] tracking-[0.22em] uppercase"
              style={{ color: "var(--br-bronze)" }}
            >
              {t("events.locked_title")}
            </div>
            <p className="text-[0.95rem] text-[color:var(--br-text-dim)] mt-4 max-w-[560px] mx-auto leading-relaxed">
              {t("events.locked_body")}
            </p>
            <Link
              to="/kyc"
              className="br-btn-outline mt-6 inline-block"
              data-testid="events-locked-cta"
            >
              {t("nav.kyc")} →
            </Link>
          </div>
        ) : (
          <>
            {error && (
              <div
                className="mt-6 br-card p-4 text-[color:var(--br-red)]"
                data-testid="events-error"
              >
                {String(error)}
              </div>
            )}

            <CriteriaCard t={t} membership={user?.membership} />

            <Section
              title={t("events.section_upcoming")}
              items={data.upcoming}
              emptyText={t("events.empty_upcoming")}
              locale={locale}
              t={t}
              testid="events-upcoming"
              onRsvpOpen={(ev) => setRsvpModal(ev)}
              onRsvpCancel={cancelRsvp}
              isPast={false}
            />

            <Section
              title={t("events.section_past")}
              items={data.past}
              emptyText={t("events.empty_past")}
              locale={locale}
              t={t}
              testid="events-past"
              onRsvpOpen={() => {}}
              onRsvpCancel={() => {}}
              isPast
            />
          </>
        )}
      </section>

      {rsvpModal && (
        <RsvpModal
          event={rsvpModal}
          onCancel={() => setRsvpModal(null)}
          onDone={() => {
            setRsvpModal(null);
            load();
          }}
          onError={(msg) => setError(msg)}
        />
      )}
    </PortalLayout>
  );
}

function CriteriaCard({ t, membership }) {
  const tier = membership?.tier || "member";
  const tierLabel = t(`membership.tier.${tier}`);
  const years = membership?.years != null ? Number(membership.years).toFixed(1) : null;
  const items = [
    { key: "member", label: t("events.criteria_member") },
    { key: "senior_member", label: t("events.criteria_senior") },
    { key: "legacy_member", label: t("events.criteria_legacy") },
    { key: "patron_member", label: t("events.criteria_patron") },
    { key: "founders_circle", label: t("events.criteria_founders") },
  ];

  return (
    <div
      className="mt-10 br-card p-6 sm:p-7 br-fade-up"
      style={{
        background: "rgba(14,20,34,0.55)",
        borderLeft: "2px solid var(--br-bronze)",
      }}
      data-testid="events-criteria-card"
    >
      <div
        className="font-mono-fin text-[0.6rem] tracking-[0.24em] uppercase"
        style={{ color: "var(--br-bronze)" }}
        data-testid="events-criteria-overline"
      >
        {t("events.criteria_overline")}
      </div>
      <h2
        className="font-serif-display text-[1.55rem] sm:text-[1.85rem] mt-2 leading-[1.18]"
        style={{ color: "var(--br-parchment)" }}
        data-testid="events-criteria-title"
      >
        {t("events.criteria_title")}
      </h2>
      <p
        className="text-[0.92rem] mt-3 leading-relaxed max-w-[720px]"
        style={{ color: "var(--br-text-dim)" }}
      >
        {t("events.criteria_intro")}
      </p>

      <ul className="mt-5 space-y-2.5" data-testid="events-criteria-list">
        {items.map((it) => {
          const isActive = it.key === tier;
          return (
            <li
              key={it.key}
              className="flex items-start gap-3 text-[0.88rem] leading-relaxed"
              style={{ color: isActive ? "var(--br-parchment)" : "var(--br-text)" }}
              data-testid={`events-criteria-item-${it.key}`}
            >
              <span
                aria-hidden
                className="mt-[7px] inline-block w-[6px] h-[6px] flex-shrink-0"
                style={{
                  background: isActive ? "var(--br-bronze-soft)" : "var(--br-line-bright)",
                  borderRadius: "1px",
                }}
              />
              <span>
                {it.label}
                {isActive && (
                  <span
                    className="ml-2 font-mono-fin text-[0.56rem] tracking-[0.22em] uppercase"
                    style={{ color: "var(--br-bronze-soft)" }}
                  >
                    · {tierLabel}
                  </span>
                )}
              </span>
            </li>
          );
        })}
      </ul>

      {years != null && (
        <div
          className="mt-5 pt-5 border-t font-mono-fin text-[0.7rem] tracking-[0.12em]"
          style={{
            borderColor: "var(--br-line)",
            color: "var(--br-text-dim)",
          }}
          data-testid="events-criteria-tenure"
        >
          {t("events.criteria_your_tenure", { years, tier: tierLabel })}
        </div>
      )}
    </div>
  );
}

function Section({ title, items, emptyText, locale, t, testid, onRsvpOpen, onRsvpCancel, isPast }) {
  return (
    <div className="mt-12 br-fade-up br-delay-1" data-testid={testid}>
      <h2 className="font-serif-display text-[1.75rem] mb-5">{title}</h2>
      {items.length === 0 ? (
        <div
          className="br-card p-6 text-[0.95rem] italic"
          style={{ color: "var(--br-text-dim)" }}
          data-testid={`${testid}-empty`}
        >
          {emptyText}
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((ev) => (
            <EventCard
              key={ev.id}
              ev={ev}
              locale={locale}
              t={t}
              onRsvpOpen={onRsvpOpen}
              onRsvpCancel={onRsvpCancel}
              isPast={isPast}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function EventCard({ ev, locale, t, onRsvpOpen, onRsvpCancel, isPast }) {
  const rsvp = ev.my_rsvp;
  const statusStyle = rsvp ? RSVP_STATUS_STYLE[rsvp.status] : null;
  return (
    <div
      className="br-card p-6"
      style={
        rsvp?.status === "accepted"
          ? { borderLeft: "3px solid var(--br-bronze-soft)" }
          : rsvp?.status === "declined"
          ? { borderLeft: "3px solid var(--br-red)" }
          : undefined
      }
      data-testid={`event-card-${ev.id}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-5">
        <div className="flex-1 min-w-[260px]">
          <div className="flex flex-wrap items-center gap-3">
            <div
              className="font-mono-fin text-[0.6rem] tracking-[0.22em] uppercase"
              style={{ color: "var(--br-bronze)" }}
              data-testid={`event-category-${ev.id}`}
            >
              {ev.category_label}
            </div>
            {rsvp && statusStyle && (
              <span
                className="font-mono-fin text-[0.6rem] tracking-[0.22em] uppercase border px-2 py-0.5"
                style={{ color: statusStyle.color, borderColor: statusStyle.border }}
                data-testid={`event-rsvp-status-${ev.id}`}
              >
                {t(`events.rsvp_status_${rsvp.status}`)}
              </span>
            )}
          </div>
          <h3 className="font-serif-display text-[1.55rem] mt-2 text-[color:var(--br-parchment)] leading-tight">
            {ev.title}
          </h3>
          {ev.description && (
            <p className="text-[0.92rem] text-[color:var(--br-text-dim)] mt-3 leading-relaxed">
              {ev.description}
            </p>
          )}

          <dl className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-3 text-[0.85rem]">
            <Field label={t("events.starts_at")} value={formatDate(ev.starts_at, locale)} mono />
            <Field
              label={t("events.venue")}
              value={[ev.location_venue, ev.location_city].filter(Boolean).join(" · ")}
            />
            {ev.dress_code && <Field label={t("events.dress_code")} value={ev.dress_code} />}
            {ev.capacity && <Field label={t("events.capacity")} value={String(ev.capacity)} mono />}
          </dl>

          {rsvp?.admin_comment && (
            <div
              className="mt-4 p-4 border-l-2"
              style={{
                borderColor: rsvp.status === "declined" ? "var(--br-red)" : "var(--br-bronze)",
                background:
                  rsvp.status === "declined"
                    ? "rgba(179,107,107,0.04)"
                    : "rgba(167,128,73,0.04)",
              }}
              data-testid={`event-admin-comment-${ev.id}`}
            >
              <div
                className="br-overline"
                style={{
                  color: rsvp.status === "declined" ? "var(--br-red)" : "var(--br-bronze)",
                }}
              >
                {t("events.rsvp_admin_comment")}
              </div>
              <p className="text-[0.92rem] font-serif-display italic mt-2 text-[color:var(--br-parchment)]">
                {`\u201C${rsvp.admin_comment}\u201D`}
              </p>
            </div>
          )}
        </div>

        {!isPast && (
          <div className="flex flex-col gap-2 min-w-[180px]">
            {!rsvp && (
              <button
                onClick={() => onRsvpOpen(ev)}
                className="br-btn-primary"
                data-testid={`event-rsvp-request-${ev.id}`}
              >
                {t("events.rsvp_request")}
              </button>
            )}
            {rsvp && rsvp.status === "pending" && (
              <button
                onClick={() => onRsvpCancel(ev.id)}
                className="br-btn-outline"
                style={{ borderColor: "var(--br-red)", color: "var(--br-red)" }}
                data-testid={`event-rsvp-cancel-${ev.id}`}
              >
                {t("events.rsvp_cancel")}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, mono = false }) {
  if (!value) return null;
  return (
    <div>
      <dt className="font-mono-fin text-[0.6rem] tracking-[0.22em] uppercase text-[color:var(--br-text-dim)]">
        {label}
      </dt>
      <dd
        className={`mt-1 text-[color:var(--br-parchment)] ${mono ? "font-mono-fin" : ""}`}
      >
        {value}
      </dd>
    </div>
  );
}

function RsvpModal({ event, onCancel, onDone, onError }) {
  const { t } = useI18n();
  const [plusOne, setPlusOne] = useState(false);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/events/${event.id}/rsvp`, {
        plus_one: plusOne,
        notes: notes.trim() || null,
      });
      onDone();
    } catch (e) {
      onError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-6 py-12 overflow-y-auto"
      style={{ background: "rgba(10,14,26,0.78)", backdropFilter: "blur(8px)" }}
      onClick={onCancel}
      data-testid="rsvp-modal"
    >
      <div
        className="br-card br-aurora-ribbon relative max-w-[560px] w-full px-9 py-10 br-fade-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="br-overline">{t("events.rsvp_modal_title")}</div>
        <h2 className="font-serif-display text-[1.9rem] mt-3 leading-[1.1]">
          {event.title}
        </h2>
        <p className="text-[0.94rem] text-[color:var(--br-text)] mt-4 leading-relaxed">
          {t("events.rsvp_modal_intro")}
        </p>

        <label className="mt-6 flex items-center gap-3 cursor-pointer" data-testid="rsvp-plusone-label">
          <input
            type="checkbox"
            checked={plusOne}
            onChange={(e) => setPlusOne(e.target.checked)}
            data-testid="rsvp-plusone"
          />
          <span className="text-[0.92rem] text-[color:var(--br-parchment)]">
            {t("events.rsvp_modal_plusone")}
          </span>
        </label>

        <label className="block mt-5">
          <span className="br-overline mb-2 block">{t("events.rsvp_modal_notes")}</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="mt-1 w-full bg-transparent border px-3 py-2 text-[0.95rem] text-[color:var(--br-parchment)] outline-none focus:border-[color:var(--br-bronze)] resize-none"
            style={{ borderColor: "var(--br-line-bright)" }}
            data-testid="rsvp-notes"
          />
        </label>

        <div className="mt-7 grid grid-cols-2 gap-3">
          <button
            onClick={onCancel}
            disabled={busy}
            className="br-btn-outline"
            data-testid="rsvp-modal-cancel"
          >
            {t("events.rsvp_modal_cancel")}
          </button>
          <button
            onClick={submit}
            disabled={busy}
            className="br-btn-primary"
            data-testid="rsvp-modal-submit"
          >
            {busy ? "…" : t("events.rsvp_modal_submit")}
          </button>
        </div>
      </div>
    </div>
  );
}
